/**
 * FMA Layout - Public-facing top navigation
 * Used for Find My Agency marketplace
 */
import { Link, Outlet, useNavigate } from "react-router-dom";
import { Suspense, useState, useEffect } from "react";
import { useConfigurationContext } from "@/global/ConfigurationContext";
import { useTheme } from "@/global/components/ui/theme-provider";
import { useAuth } from "@/global/auth/AuthContext";
import { useOrgAccess } from "@/global/hooks/useOrgAccess";
import { redirectToOtherAppWithToken } from "@/global/auth/ssoUtils";
import { Button } from "@/global/components/ui/button";
import { Toaster } from "@/global/components/ui/toaster";
import LoginModal from "@/components/forms/LoginModal";
import { pointsService } from "../../lib/pointsService";
import { usePointsToast } from "../../hooks/usePointsToast";

// FMA-specific configuration fallback (defensive coding)
const FMA_CONFIG_FALLBACK = {
  title: 'Find My Agency',
  darkModeLogo: '/logos/Orca-logo-orange-white-sm.png',
  lightModeLogo: '/logos/Orca-logo-orange-white-sm.png',
};
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/global/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/global/components/ui/sheet";
import {
  User,
  LogOut,
  ExternalLink,
  Menu,
  Building2,
  Trophy,
  GraduationCap,
  UserCircle2,
  HelpCircle,
  TrendingUp,
} from "lucide-react";
import { FloatingDock } from "../../layouts/components/FloatingDock";
import { useTour } from "@/global/contexts/TourContext";
import { useLocation } from "react-router-dom";

// Whitelisted test accounts - always show tour for testing
const TOUR_WHITELIST_EMAILS = [
  "test@orcaams.com",
  "demo@orcaams.com",
  // Add more test accounts here
];

export function FMALayout() {
  const { activeTenantId, user, loading } = useAuth();
  const { fmaPlan } = useOrgAccess();
  const { showPointsToast } = usePointsToast();
  const { startTour } = useTour();
  const location = useLocation();
  
  // Use configuration context with fallback to prevent crashes if provider is missing
  const config = useConfigurationContext() || FMA_CONFIG_FALLBACK;

  // Trigger FMA tours for first-time users or whitelisted accounts
  useEffect(() => {
    console.log('[FMALayout] Tour check:', {
      loading,
      hasUser: !!user,
      pathname: location.pathname,
    });
    
    // Don't run tour logic if auth is still loading or user is not authenticated
    if (loading || !user) {
      console.log('[FMALayout] Skipping tour - auth not ready');
      return;
    }

    const hasCompletedFMATour = user.user_metadata?.has_completed_fma_tour;
    const isWhitelisted = user.email && TOUR_WHITELIST_EMAILS.includes(user.email);
    
    console.log('[FMALayout] Tour state:', {
      hasCompletedFMATour,
      isWhitelisted,
      email: user.email,
    });
    
    // Trigger tour if:
    // 1. User hasn't completed it yet, OR
    // 2. User is whitelisted (for testing)
    const shouldShowTour = !hasCompletedFMATour || isWhitelisted;
    
    if (!shouldShowTour) {
      return;
    }

    // Map paths to tour IDs
    let tourId: "fma-home" | "fma-agencies" | "fma-training" | "fma-account" | null = null;
    
    if (location.pathname === "/" || location.pathname === "") {
      tourId = "fma-home";
    } else if (location.pathname === "/agencies") {
      tourId = "fma-agencies";
    } else if (location.pathname === "/training-vault") {
      tourId = "fma-training";
    } else if (location.pathname === "/account") {
      tourId = "fma-account";
    }
    
    if (tourId) {
      console.log(`[FMALayout] Starting ${tourId} tour in 1 second...`);
      // Delay tour start to allow page to render
      const timer = setTimeout(() => {
        console.log(`[FMALayout] Starting ${tourId} tour now`);
        startTour(tourId);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [user, loading, location.pathname, startTour]);

  // Award daily login points on mount (only for Elite organizations)
  // Delayed by 2 seconds to ensure auth is fully initialized
  useEffect(() => {
    const awardDailyLogin = async () => {
      // Only award if user is logged in and has Elite plan
      if (!activeTenantId || fmaPlan !== "fma_elite") {
        return;
      }

      try {
        const result =
          await pointsService.awardDailyLoginPoints(activeTenantId);

        // Only show toast if points were actually awarded (not already awarded today)
        if (result.success && result.points_awarded) {
          showPointsToast({
            points: result.points_awarded,
            reason: "daily_login",
            totalPoints: result.total_points,
          });
        }
      } catch (error) {
        // Silently fail - don't block user experience for points

      }
    };

    // Delay points award by 30 seconds to not impact initial page load
    const timer = setTimeout(() => {
      awardDailyLogin();
    }, 30000);

    return () => clearTimeout(timer);
  }, [activeTenantId, fmaPlan, showPointsToast]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <FMAHeader />
      <main className="container mx-auto px-4 py-8">
        <Suspense
          fallback={<div className="text-muted-foreground">Loading...</div>}
        >
          <Outlet />
        </Suspense>
      </main>
      <Toaster />
    </div>
  );
}

function FMAHeader() {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const { canAccessAMS } = useOrgAccess();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const handleAMSRedirect = async () => {
    try {
      await redirectToOtherAppWithToken("ams", undefined, "/dashboard");
    } catch (error) {

      navigate("/login");
    }
  };

  const handleLogout = () => {
    try {
      logout();
      navigate("/pricing");
    } catch (error) {

      navigate("/pricing");
    }
  };

  // Filter navigation items based on auth state
  const allNavigationItems = [
    { to: "/agencies", label: "Agencies" },
    { to: "/leaderboard", label: "Leaderboard" },
    { to: "/training-vault", label: "Training Vault" },
    { to: "/pricing", label: "Pricing", highlight: true },
    { to: "/#how-it-works", label: "How it Works", isHash: true },
    { to: "/account", label: "Account", requiresAuth: true },
  ];

  const navigationItems = allNavigationItems.filter(
    (item) => !item.requiresAuth || user
  );

  // Floating dock navigation items (desktop only)
  const dockItems = [
    {
      title: "Agencies",
      icon: <Building2 className="h-5 w-5 text-white" />,
      href: "/agencies",
    },
    {
      title: "Leaderboard",
      icon: <Trophy className="h-5 w-5 text-white" />,
      href: "/leaderboard",
      dataTour: "leaderboard-link",
    },
    {
      title: "Training Vault",
      icon: <GraduationCap className="h-5 w-5 text-white" />,
      href: "/training-vault",
    },
    {
      title: "Plans",
      icon: <TrendingUp className="h-5 w-5 text-white" />,
      href: "/pricing",
      highlight: true,
    },
    {
      title: "How it Works",
      icon: <HelpCircle className="h-5 w-5 text-white" />,
      href: "/#how-it-works",
    },
    ...(user
      ? [
          {
            title: "Account",
            icon: <UserCircle2 className="h-5 w-5 text-white" />,
            href: "/account",
            dataTour: "account-link",
          },
        ]
      : []),
  ];

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: { to: string; label: string; isHash?: boolean }
  ) => {
    if (item.isHash) {
      e.preventDefault();
      navigate("/");
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById("how-it-works");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      // For regular navigation, scroll to top immediately
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <>
      <nav
        className="sticky top-0 z-50 border-b border-white/10 bg-white/5 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/5"
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        <div className="container px-4 mx-auto">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link
                to="/"
                onClick={() => {
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }, 100);
                }}
                className="flex items-center gap-3 text-foreground no-underline hover:text-foreground/80 transition-colors"
              >
                <div className="relative">
                  <img
                    src="/logos/Orca-logo-orange-white-sm.png"
                    alt="Orca"
                    className="h-8 w-auto object-contain drop-shadow-glow"
                    width="32"
                    height="32"
                    loading="eager"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-orange-400 blur-lg opacity-30"></div>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <h1 className="text-xl font-semibold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                    Find My Agency
                  </h1>
                  <span className="text-sm font-normal text-white self-end pb-0.5">
                    from Orca
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation - Centered with FloatingDock */}
            <div className="hidden md:flex items-center absolute left-1/2 transform -translate-x-1/2">
              <FloatingDock items={dockItems} />
            </div>

            {/* Desktop Right Side */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-tour="topbar-account-link"
                      className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                    >
                      <div className="w-7 h-7 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span className="hidden sm:inline font-medium">
                        {user.user_metadata?.first_name && user.user_metadata?.last_name
                          ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                          : user.email?.split("@")[0] || "User"}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-64 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl"
                  >
                    <DropdownMenuLabel className="pb-3">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none text-white">
                          {user.user_metadata?.first_name && user.user_metadata?.last_name
                            ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                            : user.email?.split("@")[0]}
                        </p>
                        <p className="text-xs leading-none text-gray-300">
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/20" />
                    <DropdownMenuItem
                      onClick={() => navigate("/app-choice")}
                      className="cursor-pointer hover:bg-white/10 text-white focus:bg-white/10"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      <span>App Choice</span>
                    </DropdownMenuItem>
                    {canAccessAMS && (
                      <>
                        <DropdownMenuItem
                          onClick={handleAMSRedirect}
                          className="cursor-pointer hover:bg-white/10 text-white focus:bg-white/10"
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          <span>Switch to AMS</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-white/20" />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }}
                      className="cursor-pointer text-red-400 hover:bg-red-500/20 focus:bg-red-500/20"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => setShowLogin(true)}
                  variant="ghost"
                  className="px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all font-medium"
                >
                  Log In
                </Button>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center gap-2">
              {!user && (
                <Button
                  onClick={() => setShowLogin(true)}
                  variant="ghost"
                  size="sm"
                  className="px-3 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all font-medium"
                >
                  Log In
                </Button>
              )}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full hover:bg-white/10 border border-white/10"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-[300px] sm:w-[400px] bg-black/20 backdrop-blur-3xl border border-white/10 shadow-2xl"
                >
                  <SheetHeader className="pb-4 border-b border-white/10">
                    <SheetTitle className="text-2xl bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-semibold">
                      Find My Agency
                    </SheetTitle>
                    <SheetDescription className="text-white/60 text-sm">
                      Navigate to different sections
                    </SheetDescription>
                  </SheetHeader>
                  <div className="grid gap-2 py-4">
                    {navigationItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={(e) => {
                          handleNavClick(e, item);
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-4 text-lg font-medium text-white/90 hover:text-white transition-all duration-200 p-3 rounded-2xl hover:bg-white/10 active:bg-white/15 active:scale-[0.98]"
                      >
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600 opacity-60"></div>
                        {item.label}
                      </Link>
                    ))}

                    {user && (
                      <>
                        <div className="border-t border-white/10 pt-4 mt-4 px-2">
                          <div className="flex items-center gap-4 mb-4 p-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                            <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                              <User className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex flex-col">
                              <p className="text-base font-semibold leading-none text-white">
                                {user.user_metadata?.first_name && user.user_metadata?.last_name
                                  ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                                  : user.email?.split("@")[0]}
                              </p>
                              <p className="text-sm leading-none text-white/60 mt-1">
                                {user.email}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Button
                              onClick={() => {
                                navigate("/app-choice");
                                setIsMobileMenuOpen(false);
                              }}
                              variant="outline"
                              className="w-full justify-start gap-3 h-11 bg-white/5 backdrop-blur-sm border-white/20 text-white hover:bg-white/10 rounded-2xl font-medium transition-all duration-200 active:scale-[0.98]"
                            >
                              <ExternalLink className="h-5 w-5" />
                              App Choice
                            </Button>

                            {canAccessAMS && (
                              <Button
                                onClick={() => {
                                  handleAMSRedirect();
                                  setIsMobileMenuOpen(false);
                                }}
                                variant="outline"
                                className="w-full justify-start gap-3 h-11 bg-white/5 backdrop-blur-sm border-white/20 text-white hover:bg-white/10 rounded-2xl font-medium transition-all duration-200 active:scale-[0.98]"
                              >
                                <ExternalLink className="h-5 w-5" />
                                Switch to AMS
                              </Button>
                            )}

                            <Button
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                handleLogout();
                              }}
                              variant="destructive"
                              className="w-full justify-start gap-3 h-11 bg-red-500/10 backdrop-blur-sm border border-red-500/20 text-red-400 hover:bg-red-500/20 rounded-2xl font-medium transition-all duration-200 active:scale-[0.98]"
                            >
                              <LogOut className="h-5 w-5" />
                              Log out
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Login Modal - Outside nav to avoid z-index issues */}
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </>
  );
}
